/**
 * Brown Butters — shoppable reels.
 *
 * Two jobs, both deliberately small:
 *   1. Sound toggle. Reel videos autoplay muted (browsers refuse anything else), so the
 *      circle button on the video is the only way a shopper can hear one.
 *   2. Only play what is on screen. Five simultaneous 9:16 videos is a lot of decode work,
 *      so anything scrolled out of view is paused and re-muted.
 *
 * Dependency-free: it talks to the DOM and nothing else.
 */

const CARD_SELECTOR = '[data-mbb-reel]';
const SOUND_SELECTOR = '[data-mbb-reel-sound]';

class MbbReels extends HTMLElement {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);

    if (!('IntersectionObserver' in window)) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const video = entry.target;
          if (!(video instanceof HTMLVideoElement)) continue;

          if (entry.isIntersecting) {
            if (video.autoplay) video.play().catch(() => {});
          } else {
            video.pause();
            this.#setMuted(video, true);
          }
        }
      },
      { threshold: 0.35 }
    );

    for (const video of this.querySelectorAll('video')) {
      this.#observer.observe(video);
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#observer?.disconnect();
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest(SOUND_SELECTOR);
    if (!button) return;

    const video = button.closest(CARD_SELECTOR)?.querySelector('video');
    if (!video) return;

    event.preventDefault();

    if (video.muted) {
      // Only one reel is audible at a time.
      for (const other of this.querySelectorAll('video')) {
        if (other !== video) this.#setMuted(other, true);
      }
      this.#setMuted(video, false);
      video.play().catch(() => {});
    } else {
      this.#setMuted(video, true);
    }
  };

  /**
   * @param {HTMLVideoElement} video
   * @param {boolean} muted
   */
  #setMuted(video, muted) {
    video.muted = muted;
    const button = video.closest(CARD_SELECTOR)?.querySelector(SOUND_SELECTOR);
    button?.setAttribute('aria-pressed', muted ? 'false' : 'true');
  }
}

if (!customElements.get('mbb-reels')) {
  customElements.define('mbb-reels', MbbReels);
}
